import { Directive, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

/**
 * Reports whether a clamped element is actually hiding anything.
 *
 * MEASURED, NOT COUNTED FROM THE STRING. A "Read more" that appears on character count lies in
 * both directions, and it lies worst in the languages we cannot check by eye: across the app's own
 * parallel bundles, Macedonian runs only ~8% longer than English in characters but takes ~35% more
 * rendered LINES, because Poppins carries no Cyrillic and the substituted face is wider. A budget
 * that looked right in English would offer "Read more" on copy that fits, and hide copy that does
 * not, for every Cyrillic venue. Only the laid-out box knows. @see [[poppins-has-no-cyrillic]]
 *
 * ⚠️ WHY THIS CANNOT OSCILLATE, unlike `CompactTitleDirective` before its fix. That one set a class
 * that resized the very box it measured, so its answer changed its own input. This one only
 * reports; the clamp above it is a fixed LINE COUNT, so `clientHeight` does not move when the
 * answer changes, and the control this drives is rendered outside the observed element. If you
 * ever make the clamp depend on available height instead of a line count, that stops being true
 * and this needs the same one-way treatment the title directive got.
 */
@Directive({
    selector: '[appOverflows]',
    standalone: false
})
export class TextOverflowDirective implements OnInit, OnDestroy {
  /** Emits when the answer changes, not on every frame. */
  @Output('appOverflows') overflows = new EventEmitter<boolean>();

  private observer?: ResizeObserver;
  private last: boolean | null = null;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngOnInit(): void {
    // Without ResizeObserver the control simply never appears, which leaves the old behaviour
    // (clamped text, no way to open it) rather than a broken one.
    if (typeof ResizeObserver === 'undefined') return;

    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.host.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private measure(): void {
    const el = this.host.nativeElement;

    // One pixel of slack: a clamped box routinely measures a fraction over its own line boxes, and
    // a bare `>` would call every fitting paragraph truncated.
    const overflows = el.scrollHeight > el.clientHeight + 1;
    if (overflows === this.last) return;
    this.last = overflows;

    // The observer runs outside Angular and this drives a control in the template.
    this.zone.run(() => this.overflows.emit(overflows));
  }
}
